import type {
  EventTicketSummary,
  TicketOrderCheckIn,
  TicketOrderListStatus,
  TicketOrderSort,
} from '@pulsify/shared/dto'
import { Download, Search, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Input } from '~/shared/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/shared/ui/select'
import { hasFilters, refineSearch, type TicketOrdersSearch } from '../lib/order-search'

const SEARCH_DELAY_MS = 300
const ALL = 'all'

type Option = { value: string; label: string }

function FilterSelect({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string
  value: string
  options: Option[]
  onChange: (value: string) => void
  className?: string
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        size="sm"
        aria-label={label}
        className={cn(
          'rounded-full border-foreground/15 bg-card hover:border-foreground/25',
          value !== ALL && value !== 'newest' && 'border-foreground/40 text-foreground',
          className,
        )}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent align="start">
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function useDebouncedSearch(
  search: TicketOrdersSearch,
  onSearchChange: (search: TicketOrdersSearch) => void,
) {
  const [text, setText] = useState(search.q ?? '')
  const latest = useRef({ search, onSearchChange })

  useEffect(() => {
    latest.current = { search, onSearchChange }
  })

  useEffect(() => {
    const q = search.q ?? ''
    setText((current) => (current.trim() === q ? current : q))
  }, [search.q])

  useEffect(() => {
    const next = text.trim()
    if (next === (latest.current.search.q ?? '')) return
    const timer = window.setTimeout(() => {
      const current = latest.current
      current.onSearchChange(refineSearch(current.search, { q: next || undefined }))
    }, SEARCH_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [text])

  return [text, setText] as const
}

export function OrderToolbar({
  search,
  types,
  onSearchChange,
  onExport,
  exporting,
  canExport,
}: {
  search: TicketOrdersSearch
  types: EventTicketSummary['types']
  onSearchChange: (search: TicketOrdersSearch) => void
  onExport: () => void
  exporting: boolean
  canExport: boolean
}) {
  const [text, setText] = useDebouncedSearch(search, onSearchChange)
  const refine = (change: Parameters<typeof refineSearch>[1]) =>
    onSearchChange(refineSearch(search, change))

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
      <div className="relative min-w-0 lg:w-80">
        <Search
          aria-hidden
          className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 size-4 text-muted-foreground"
        />
        <Input
          type="search"
          value={text}
          onChange={(event) => setText(event.target.value)}
          aria-label={m.ticketPageSearchLabel()}
          placeholder={m.ticketPageSearchPlaceholder()}
          maxLength={100}
          className="h-9 rounded-full pl-9 [&::-webkit-search-cancel-button]:hidden"
        />
        {text ? (
          <button
            type="button"
            aria-label={m.ticketPageReset()}
            onClick={() => {
              setText('')
              refine({ q: undefined })
            }}
            className="-translate-y-1/2 absolute top-1/2 right-2 inline-flex size-6 items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/[0.06] hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {types.length > 1 ? (
          <FilterSelect
            label={m.ticketPageColumnType()}
            value={search.type ?? ALL}
            onChange={(value) => refine({ type: value === ALL ? undefined : value })}
            options={[
              { value: ALL, label: m.ticketPageFilterTypeAll() },
              ...types.map((type) => ({ value: type.id, label: type.name })),
            ]}
            className="max-w-48"
          />
        ) : null}
        <FilterSelect
          label={m.ticketPageFilterStatus()}
          value={search.status ?? ALL}
          onChange={(value) =>
            refine({ status: value === ALL ? undefined : (value as TicketOrderListStatus) })
          }
          options={[
            { value: ALL, label: m.ticketPageFilterStatusAll() },
            { value: 'paid', label: m.ticketsOrderPaid() },
            { value: 'free', label: m.ticketsOrderFree() },
            { value: 'refunded', label: m.ticketsOrderRefunded() },
          ]}
        />
        <FilterSelect
          label={m.ticketPageFilterCheckIn()}
          value={search.checkIn ?? ALL}
          onChange={(value) =>
            refine({
              checkIn: value === ALL ? undefined : (value as TicketOrderCheckIn),
            })
          }
          options={[
            { value: ALL, label: m.ticketPageFilterCheckInAll() },
            { value: 'checked_in', label: m.ticketPageCheckedIn() },
            { value: 'missing', label: m.ticketPageFilterMissing() },
          ]}
        />
        <FilterSelect
          label={m.ticketPageSortLabel()}
          value={search.sort ?? 'newest'}
          onChange={(value) => refine({ sort: value as TicketOrderSort })}
          options={[
            { value: 'newest', label: m.ticketPageSortNewest() },
            { value: 'name', label: m.ticketPageSortName() },
            { value: 'amount', label: m.ticketPageSortAmount() },
          ]}
        />
        {hasFilters(search) ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setText('')
              onSearchChange(search.sort ? { sort: search.sort } : {})
            }}
          >
            {m.ticketPageReset()}
          </Button>
        ) : null}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="self-start lg:ml-auto lg:self-auto"
        disabled={!canExport || exporting}
        onClick={onExport}
      >
        <Download />
        {m.ticketPageExport()}
      </Button>
    </div>
  )
}
